<template>
  <el-select
    v-model="valueBackup"
    :placeholder="placeholderBackup"
    :size="size"
    :clearable="clearable"
    :multiple="multiple"
    :multiple-limit="multiple ? maxCount : 0"
    :disabled="disabled || !studentId"
    :loading="isLoading"
    @change="handleChange"
  >
    <el-option
      v-for="item in listBackup"
      :key="item.id"
      :label="optionLabel(item)"
      :value="item.id"
      :disabled="isOptionDisabled(item)"
    />
  </el-select>
</template>

<script>
import { getAvailableByStudentId } from '@/api/voucher';
import { toYuan } from '@/filters';

export default {
  name: 'VSelectVoucher',
  props: {
    value: [Array, Number, String],
    studentId: [Number, String],
    size: {
      type: String,
      default: 'medium',
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    disabled: Boolean,
    // 多选：办卡/办卡申请可一次选择多张券（仅可叠加券能共存）
    multiple: Boolean,
    // 多选时最多可选张数
    maxCount: {
      type: Number,
      default: 5,
    },
    cardChargeTypeId: {
      type: [Number, String],
      default: 0,
    },
    storeId: {
      type: [Number, String],
      default: 0,
    },
    placeholder: {
      type: String,
      default: '请选择代金券（可选）',
    },
    emptyPlaceholder: {
      type: String,
      default: '暂无可用代金券',
    },
    noStudentPlaceholder: {
      type: String,
      default: '请先选择学员',
    },
  },
  data: () => ({
    valueBackup: [],
    listBackup: [],
    isLoading: false,
  }),
  computed: {
    placeholderBackup() {
      if (!this.studentId) return this.noStudentPlaceholder;
      if (!this.isLoading && !this.listBackup.length) return this.emptyPlaceholder;
      return this.placeholder;
    },
  },
  watch: {
    value(value) {
      if (this.isSameValue(value, this.valueBackup)) return;
      this.valueBackup = this.normalizeValue(value);
    },
    valueBackup(valueBackup) {
      if (this.isSameValue(valueBackup, this.value)) return;
      this.$emit('input', this.normalizeEmit(valueBackup));
    },
    studentId(studentId, oldStudentId) {
      if (studentId === oldStudentId) return;
      this.getList(studentId);
    },
    cardChargeTypeId() {
      this.getList(this.studentId);
    },
    storeId() {
      this.getList(this.studentId);
    },
  },
  methods: {
    toYuan,
    // 把父组件的值归一化成本组件内部使用的形态（多选为 int 数组，单选为 int 或 ''）
    normalizeValue(value) {
      if (!this.multiple) return Number(value) || '';
      const list = Array.isArray(value) ? value : (value ? [value] : []);
      return list.map(id => Number(id)).filter(Boolean);
    },
    // 把内部值归一化成对外 emit 的形态（多选为 int 数组，单选为 int 或 ''）
    normalizeEmit(valueBackup) {
      if (!this.multiple) return valueBackup;
      return (Array.isArray(valueBackup) ? valueBackup : []).map(id => Number(id)).filter(Boolean);
    },
    isSameValue(a, b) {
      if (this.multiple) {
        const x = this.normalizeEmit(a);
        const y = this.normalizeEmit(b);
        return x.length === y.length && x.every((id, index) => id === y[index]);
      }
      return (Number(a) || '') === (Number(b) || '');
    },
    optionLabel(item) {
      let label = `${item.voucher_type_name} - ${toYuan(item.amount_in_cents)}`;
      if (item.is_store_limited) label += `（仅限${item.limit_store_name}）`;
      if (item.is_occupied) label += ' （已被其他办卡申请占用）';
      if (item.is_compatible === 0) label += ' （不适用于当前套餐）';
      if (this.multiple && Number(item.is_stackable) === 0) label += ' （不可叠加）';
      return label;
    },
    // 已选中不可叠加券时其余全禁；选中的可叠加券之间可继续加选；同类型只允许一张
    isOptionDisabled(item) {
      if (item.is_occupied || item.is_compatible === 0) return true;
      if (!this.multiple) return false;

      const selected = this.valueBackup || [];
      if (selected.includes(item.id)) return false;
      if (!selected.length) return false;

      const selectedItems = selected
        .map(id => this.listBackup.find(i => i.id === id))
        .filter(Boolean);
      if (selectedItems.some(i => !Number(i.is_stackable))) return true;
      if (!Number(item.is_stackable)) return true;
      if (selectedItems.some(i => i.voucher_type_id === item.voucher_type_id)) return true;
      return false;
    },
    handleChange(valueBackup) {
      if (this.multiple) {
        const list = (valueBackup || [])
          .map(id => this.listBackup.find(i => i.id === id))
          .filter(Boolean);
        return this.$emit('change', list);
      }

      if (!valueBackup) return this.$emit('change', null);

      for (let i = 0; i < this.listBackup.length; i++) {
        if (this.listBackup[i].id === valueBackup) return this.$emit('change', this.listBackup[i]);
      }

      return this.$emit('change', null);
    },
    // 列表刷新后（切学员/套餐/门店）剔除已失效的选中项，并保证叠加规则成立
    trimSelection() {
      const selected = this.valueBackup || [];
      if (!selected.length) return;

      const validItems = [];
      for (const id of selected) {
        const item = this.listBackup.find(i => i.id === id);
        if (!item || item.is_occupied || item.is_compatible === 0) continue;
        if (validItems.length) {
          if (!Number(item.is_stackable)) continue;
          if (!Number(validItems[0].is_stackable)) continue;
          if (validItems.some(v => v.voucher_type_id === item.voucher_type_id)) continue;
        }
        validItems.push(item);
      }

      const validIds = validItems.map(i => i.id);
      if (!this.isSameValue(validIds, selected)) {
        this.valueBackup = validIds;
        // 程序性剔除失效券时也要通知父级重算合计与实收（change 默认只在用户操作时触发）
        this.handleChange(validIds);
      }
    },
    async getList(studentId) {
      if (!studentId) {
        this.listBackup = [];
        if (this.multiple) {
          if ((this.valueBackup || []).length) this.valueBackup = [];
        } else if (this.valueBackup) {
          this.valueBackup = '';
        }
        return;
      }

      this.isLoading = true;
      const params = { student_id: studentId };
      if (this.cardChargeTypeId) params.card_charge_type_id = this.cardChargeTypeId;
      if (this.storeId) params.store_id = this.storeId;
      const { isError, data } = await getAvailableByStudentId(params);
      this.isLoading = false;

      this.listBackup = isError ? [] : (data || []);

      if (this.multiple) {
        this.trimSelection();
      } else if (this.valueBackup) {
        const selected = this.listBackup.find(item => item.id === this.valueBackup);
        if (!selected || selected.is_occupied || selected.is_compatible === 0) {
          this.valueBackup = '';
        }
      }
    },
  },
  beforeMount() {
    this.valueBackup = this.normalizeValue(this.value);
    this.getList(this.studentId);
  },
};
</script>
